var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useLayer } from 'react-laag';
import { createNodesByKeyMap } from '../../../../ui-toolkit/elements/tree-view/TreeView.helpers';
export var useTreeSelectState = function (nodes, value, isDisabled, inputElementRef, treeViewContainerRef, onChange) {
    if (isDisabled === void 0) { isDisabled = false; }
    var isControlled = value !== undefined;
    var _a = useState(false), isSelectOpen = _a[0], setIsSelectOpen = _a[1];
    var _b = useState(''), filterValue = _b[0], setFilterValue = _b[1];
    var _c = useLayer({
        isOpen: isSelectOpen,
        onOutsideClick: function () { return setIsSelectOpen(false); },
        placement: 'bottom-start',
        auto: true,
        triggerOffset: 16,
    }), renderLayer = _c.renderLayer, triggerProps = _c.triggerProps, layerProps = _c.layerProps;
    var nodesByKey = React.useMemo(function () { return createNodesByKeyMap(nodes); }, [nodes]);
    var _d = useState(function () { return value !== null && value !== void 0 ? value : []; }), selectedKeysInternal = _d[0], setSelectedKeysInternal = _d[1];
    var selectedNodesInternal = useMemo(function () { return _.map(selectedKeysInternal, function (key) { return nodesByKey.get(key); }); }, [selectedKeysInternal]);
    useEffect(function () {
        if (!value) {
            return;
        }
        setSelectedKeysInternal(function () { return value !== null && value !== void 0 ? value : []; });
    }, [nodesByKey, value]);
    var handleOnChange = function (selectedKeys) {
        if (!isControlled) {
            setSelectedKeysInternal(function () { return selectedKeys; });
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(selectedKeys);
    };
    var handleNodeSelect = function (selectedNodes, unselectedNodes) {
        var _a;
        if (isDisabled) {
            return;
        }
        var selectedKeys = _.map(selectedNodes, 'key');
        var unselectedKeys = _.map(unselectedNodes, 'key');
        handleOnChange((_a = _(selectedKeysInternal)
            .concat(selectedKeys))
            .without.apply(_a, unselectedKeys).value());
    };
    var handleNodeUnselect = function (unselectedNodes) {
        if (isDisabled) {
            return;
        }
        var keys = _.map(unselectedNodes, 'key');
        handleOnChange(_.without.apply(_, __spreadArray([selectedKeysInternal], keys, false)));
    };
    var handleNodeRemoveClick = function (e, node) {
        // HACK: Prevents a mysterious issue where clicking the remove button on the badge would also trigger the
        //  click event for the next badge in line, causing two badges to be removed instead of one.
        e.preventDefault();
        if (isDisabled) {
            return;
        }
        var selectedKeys = _.without(selectedKeysInternal, node.key);
        if (!isControlled) {
            setSelectedKeysInternal(function () { return selectedKeys; });
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(selectedKeys);
    };
    var handleInputContainerFocus = function () {
        var _a;
        if (isDisabled) {
            return;
        }
        setIsSelectOpen(true);
        (_a = inputElementRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    var handleInputKeyDown = function (e) {
        var _a, _b;
        switch (e.key) {
            case 'Escape': {
                setIsSelectOpen(false);
                break;
            }
            case 'Backspace': {
                // If there was no filter value, remove the last selected node
                if (_.isEmpty(filterValue)) {
                    handleOnChange(_.initial(selectedKeysInternal));
                }
                break;
            }
            case 'ArrowDown': {
                // Focus the first checkbox in the tree view
                (_b = (_a = treeViewContainerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('input[type="checkbox"]')) === null || _b === void 0 ? void 0 : _b.focus();
                break;
            }
        }
    };
    var handleClearInput = function () {
        if (isDisabled) {
            return;
        }
        if (!isControlled) {
            setSelectedKeysInternal(function () { return []; });
        }
        onChange === null || onChange === void 0 ? void 0 : onChange([]);
    };
    return {
        isSelectOpen: isSelectOpen,
        setIsSelectOpen: setIsSelectOpen,
        filterValue: filterValue,
        setFilterValue: setFilterValue,
        handleClearInput: handleClearInput,
        handleInputContainerFocus: handleInputContainerFocus,
        handleInputKeyDown: handleInputKeyDown,
        handleNodeRemoveClick: handleNodeRemoveClick,
        handleNodeSelect: handleNodeSelect,
        handleNodeUnselect: handleNodeUnselect,
        handleOnChange: handleOnChange,
        renderLayer: renderLayer,
        triggerProps: triggerProps,
        layerProps: layerProps,
        nodesByKey: nodesByKey,
        selectedKeysInternal: selectedKeysInternal,
        selectedNodesInternal: selectedNodesInternal,
    };
};
